import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["navMarker", "errorMarker", "title", "description", "use", "showDescription", "appearsIn", "showOverview", "showPagination", "size", "onItemClick", "direction", "resultTypes"];
import { useCallback, useState } from 'react';
import { CardsSection } from '../../cardsSection/components/CardsSection';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { useAiRecommendationsForLearningContent } from '../../academyAi';
import getLang from 'academy-common-lib/utils/getLang';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useRankedRecommendationsContent } from 'academy-common-lib/features/recommendations/hooks/useRankedRecommendationsContent';
import Raven from 'raven-js';
import { usePostRankedRecommendationsFeedback } from 'academy-common-lib/features/recommendations/hooks/usePostRankedRecommendationsFeedback';
import { useRecommendationsState } from '../recommendationsStore';
import { jsx as _jsx } from "react/jsx-runtime";
const useRecommendationsForShorts = ({
  item,
  maxCards,
  offset,
  skip
}) => {
  const {
    recommendations,
    recommendationsLoading,
    recommendationsError,
    hasMore,
    nextOffset,
    correlationId
  } = useAiRecommendationsForLearningContent({
    item,
    size: maxCards,
    skip: !maxCards || skip,
    offset
  });

  // retrieves hydrated card models for the elements to render
  const {
    cardModels,
    cardModelsError,
    cardModelsLoading
  } = useLearningContentCardModels({
    items: (recommendations === null || recommendations === void 0 ? void 0 : recommendations.map(({
      recommendationId,
      recommendationType,
      language
    }) => {
      return {
        id: recommendationId,
        type: recommendationType,
        language: language || getLang()
      };
    })) || [],
    skip: !(recommendations !== null && recommendations !== void 0 && recommendations.length)
  });
  return {
    recommendationsForShorts: cardModels,
    recommendationsForShortsLoading: recommendationsLoading || cardModelsLoading || maxCards === undefined,
    recommendationsForShortsError: recommendationsError || cardModelsError,
    hasMore,
    nextOffset,
    recommendationsForShortsCorrelationId: correlationId
  };
};
const useRecommendationsForLearningContent = ({
  item,
  maxCards,
  offset,
  types: resultTypes
}) => {
  const isShort = item.type === 'SHORT_FORM_CONTENT';

  // shorts uses the similarity recommendations API based on VaaS
  const {
    recommendationsForShorts,
    recommendationsForShortsLoading,
    recommendationsForShortsError,
    recommendationsForShortsCorrelationId,
    nextOffset,
    hasMore
  } = useRecommendationsForShorts({
    item,
    maxCards,
    offset,
    skip: !isShort
  });

  // courses and lessons recommendations uses the ranked recommendations API
  const {
    content: rankedRecommendations,
    loading: rankedRecommendationsLoading,
    error: rankedRecommendationsError,
    correlationId: rankedRecommendationsCorrelationId
  } = useRankedRecommendationsContent({
    language: item.language,
    maxCards,
    types: resultTypes,
    inferenceSource: item.type === 'TRACK' ? 'COURSE_RECOMMENDATIONS' : 'LESSON_RECOMMENDATIONS',
    skip: isShort
  });
  if (isShort) {
    return {
      content: recommendationsForShorts,
      recommendationsError: recommendationsForShortsError,
      recommendationsLoading: recommendationsForShortsLoading,
      correlationId: recommendationsForShortsCorrelationId,
      nextOffset,
      hasMore
    };
  } else {
    return {
      content: rankedRecommendations,
      recommendationsError: rankedRecommendationsError,
      recommendationsLoading: rankedRecommendationsLoading,
      correlationId: rankedRecommendationsCorrelationId,
      nextOffset,
      hasMore
    };
  }
};
export const RecommendationsForLearningContent = _ref => {
  let {
      navMarker = 'RECOMMENDATIONS_FOR_ENTITY_LOADED',
      errorMarker = 'RECOMMENDATIONS_FOR_ENTITY_API_ERROR',
      title,
      description,
      use,
      showDescription,
      appearsIn,
      showOverview,
      showPagination = true,
      size = 'large',
      onItemClick,
      direction,
      resultTypes
    } = _ref,
    item = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [maxCards, setMaxCards] = useState();
  const {
    isDesktop
  } = useMediaQuery();
  const [offset, setOffset] = useState('');
  const [previousOffsets, setPreviousOffsets] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const {
    content,
    recommendationsError,
    recommendationsLoading,
    correlationId,
    nextOffset,
    hasMore
  } = useRecommendationsForLearningContent({
    item,
    maxCards,
    offset,
    types: resultTypes
  });
  const {
    postRankedRecommendationsFeedback
  } = usePostRankedRecommendationsFeedback();
  const {
    setLastClickedRecommendationItem
  } = useRecommendationsState();
  const previousOffset = previousOffsets[currentPage - 1];
  const handleClickNext = useCallback(() => {
    if (previousOffsets.length <= currentPage) setPreviousOffsets(state => [...state, offset]);
    setOffset(nextOffset || '');
    setCurrentPage(cp => cp + 1);
  }, [currentPage, nextOffset, offset, previousOffsets.length]);
  const handleClickPrevious = useCallback(() => {
    setOffset(previousOffset);
    setCurrentPage(cp => cp - 1);
  }, [previousOffset]);
  const handleOnItemClick = useCallback((cardModel, index) => {
    // VaaS APIs (shorts) don't need to send feedback for the clicked or engaged item, only ranked recommendations do
    if (correlationId && content && item.type !== 'SHORT_FORM_CONTENT') {
      postRankedRecommendationsFeedback({
        entityType: cardModel.type,
        entityId: cardModel.id,
        correlationId,
        content: content
      }).catch(Raven.captureException.bind(Raven));
      setLastClickedRecommendationItem({
        correlationId,
        item: {
          id: cardModel.id,
          type: cardModel.type
        },
        recommendationEngine: 'ranked'
      });
    }
    onItemClick === null || onItemClick === void 0 || onItemClick(cardModel, index, correlationId);
  }, [correlationId, content, item.type, onItemClick, postRankedRecommendationsFeedback, setLastClickedRecommendationItem]);
  return /*#__PURE__*/_jsx(CardsSection, {
    appearsIn: appearsIn || 'recommendations-for-entity',
    content: content,
    description: description,
    direction: direction,
    error: recommendationsError,
    errorMarker: errorMarker,
    header: title,
    isLoading: recommendationsLoading,
    navMarker: navMarker,
    onClickNext: handleClickNext,
    onClickPrevious: handleClickPrevious,
    onItemClick: handleOnItemClick,
    setMaxCards: setMaxCards,
    showDescription: showDescription,
    showNext: showPagination && hasMore && !!nextOffset && isDesktop,
    showOverview: showOverview,
    showPrevious: showPagination && currentPage > 0 && isDesktop,
    size: size,
    use: use
  });
};